import React from "react";

import type { TelemetryBudgetMeta, TelemetryQueryMeta } from "@/lib/telemetry";
import { formatBytes, formatDurationMs, formatUsd } from "@/components/telemetry/format";
import { useT } from "@/app/localization";

/**
 * What this screen cost. Small, but never hidden.
 *
 * Shown for cache hits too: a report that only counts the misses cannot answer
 * "why did the bill grow", and the 10 MB per-query floor means the cheap-looking
 * frequent queries are the ones that add up.
 */
export function QueryCost({
  queries,
  budget,
  mode,
  truncated,
}: {
  queries: TelemetryQueryMeta[];
  budget?: TelemetryBudgetMeta;
  mode?: string;
  truncated?: boolean;
}) {
  const t = useT();
  const scans = queries.filter(Boolean);
  if (!scans.length) return null;

  const bytes = scans.reduce((sum, q) => sum + (q.bytes_scanned || 0), 0);
  const cost = scans.reduce((sum, q) => sum + (q.estimated_cost_usd || 0), 0);
  const slowest = Math.max(...scans.map((q) => q.duration_ms || 0));
  // Distinct by the API's cache code; an unknown code is shown raw.
  const caches = Array.from(new Set(scans.map((q) => q.cache))).map((cache) =>
    t("operator.telemetry.cache_kind", { cache, raw: cache }),
  );
  const queryIds = scans.map((q) => q.athena_query_id).filter(Boolean) as string[];

  return (
    <div
      data-testid="telemetry-query-cost"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground"
    >
      <span>
        {scans.length > 1
          ? t("operator.telemetry.scanned_many", { count: scans.length, bytes: formatBytes(bytes) })
          : t("operator.telemetry.scanned", { bytes: formatBytes(bytes) })}
      </span>
      <span>{t("operator.telemetry.estimated_cost", { cost: formatUsd(cost) })}</span>
      <span>{caches.join(" + ")}</span>
      <span>{formatDurationMs(slowest)}</span>
      {mode && <span>{t("operator.telemetry.query_mode", { mode })}</span>}
      {truncated && <span className="text-amber-600 dark:text-amber-400">{t("operator.telemetry.result_truncated")}</span>}
      {budget && (
        <span>
          {t("operator.telemetry.budget_today", {
            spent: formatBytes(budget.spent_bytes_today),
            limit: formatBytes(budget.limit_bytes),
          })}
        </span>
      )}
      {/* Keyed by position: two scans of the same window legitimately report the
          same query id when Athena reuses a previous result. */}
      {queryIds.map((id, index) => (
        <span key={`${id}-${index}`} className="font-mono opacity-70" title={t("operator.telemetry.query_id")}>
          {id.slice(0, 8)}…
        </span>
      ))}
    </div>
  );
}
